import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Video, MapPin, Phone, Navigation, Mic, ShieldAlert, ArrowRight, Bell, BatteryCharging } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const features = [
  { icon: Video, title: "Live Stream", desc: "See and hear their world in real time, straight from the glasses." },
  { icon: MapPin, title: "Live Location", desc: "Track their exact location on a beautiful in-app map, 24/7." },
  { icon: Phone, title: "Two-Way Voice", desc: "Tap once to talk. No calling, no waiting — just connect." },
  { icon: Navigation, title: "Take Me Home", desc: "Guided navigation home with gentle, spoken turn-by-turn cues." },
  { icon: Mic, title: "Voice Command", desc: "Hands-free control. Just say it and the on-device assistant listens." },
  { icon: ShieldAlert, title: "Emergency SOS", desc: "One tap triggers 911 and instantly alerts every guardian." },
];

export default function AppPage() {
  const navigate = useNavigate();
  return (
    <div data-testid="app-page" className="bg-white">
      {/* Hero */}
      <section className="bg-zinc-950 text-white pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The Vsense App</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
              One app.<br />Total peace of mind.
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/60 max-w-md mx-auto leading-relaxed">
              Everything you need to stay connected to the people you love — live location,
              two-way voice, streaming and emergency alerts — in one beautifully simple guardian app.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button
                data-testid="app-hero-reserve"
                onClick={() => navigate("/shop")}
                className="group inline-flex items-center gap-2 bg-white text-zinc-900 text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-zinc-200 active:scale-[0.98] transition-all duration-200"
              >
                Reserve your glasses
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <span className="text-sm text-white/40">Free on iOS & Android</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Full-scale app showcase — readable screens */}
      <section className="bg-zinc-950 text-white pb-20 md:pb-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Reveal>
            <motion.img
              initial={{ scale: 0.97, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease }}
              src="/products/app-phones.jpg"
              alt="Full-scale view of the Vsense guardian app screens"
              className="w-full rounded-3xl"
              data-testid="app-fullscale-image"
            />
          </Reveal>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900">
              Everything, in one glance.
            </h2>
            <p className="mt-4 text-base md:text-lg text-zinc-500">
              Open the app and you're already there — no menus to dig through, no setup to fumble with.
            </p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.05}>
                <div data-testid={`app-feature-${i}`} className="flex flex-col gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-zinc-900 text-white flex items-center justify-center">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900">{f.title}</h3>
                    <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Live location split */}
      <section className="bg-[#F5F5F7] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Always know where</p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-tight">
              Live location,<br />on a map you love.
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed max-w-md">
              Watch them move in real time, set safe zones, and get an instant nudge the moment
              they arrive or leave. It's the kind of quiet reassurance you feel all day long.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              {["Real-time GPS updates", "Geofenced safe zones & arrival alerts", "Location history at a glance"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-900" />
                  <span className="text-sm md:text-base">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] overflow-hidden bg-white aspect-[4/3] shadow-[0_24px_60px_rgb(0,0,0,0.08)]">
              <img src="/products/app-map.jpg" alt="Vsense app live location map" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Home dashboard split */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal className="md:order-2">
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Designed to disappear</p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-tight">
              Calm by design.
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed max-w-md">
              A home screen that shows only what matters — who's online, where they are, and one tap
              to reach them. Notifications you can trust, and nothing you don't need.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {[
                { icon: Bell, title: "Smart alerts", desc: "Only the notifications that matter." },
                { icon: BatteryCharging, title: "Battery status", desc: "See charge for every pair." },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5 text-zinc-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-zinc-900">{f.title}</div>
                    <div className="text-sm text-zinc-500">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:order-1">
            <div className="rounded-[2rem] overflow-hidden bg-[#F5F5F7] aspect-[4/3] shadow-[0_24px_60px_rgb(0,0,0,0.08)]">
              <img src="/products/app-home.jpg" alt="Vsense app home dashboard" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">
              The app is free.<br />The peace of mind is priceless.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">
              Reserve your Vsense glasses today — no payment, no commitment — and lock in 40% off at launch.
            </p>
            <button
              data-testid="app-cta-reserve"
              onClick={() => navigate("/shop")}
              className="mt-9 inline-flex items-center gap-2 bg-white text-zinc-900 text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-200 active:scale-[0.98] transition-all duration-200"
            >
              Reserve now <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
